import { useState, useEffect, useMemo } from 'react';
import { TypeElement } from 'typescript';
import firebase, { db } from 'utils/firebase';
import logFirestoreError from 'utils/logFirestoreError';

type WhereFilterOp =
  | '<'
  | '<='
  | '=='
  | '!='
  | '>='
  | '>'
  | 'array-contains'
  | 'in'
  | 'array-contains-any'
  | 'not-in';

type Triplet = [string, WhereFilterOp, any];

interface Options {
  filter?: Triplet[];
  sort?: [string, string] | [];
  limit?: null | number;
  keepDataWhileLoading?: boolean;
}

type Type<T = any> = [
  {
    ref: any;
    data: (T & { id: string })[];
    loading: boolean;
    error: null | string;
  },
  React.Dispatch<React.SetStateAction<(T & { id: string })[]>>
];

function useCollection<T = any>(
  collectionPath: string = '',
  options: Options = { filter: [], sort: [], limit: null }
): Type<T> {
  const { filter = [], sort = [], limit = null } = options;

  const [data, setdata] = useState<(T & { id: string })[]>([]);
  const [loading, setloading] = useState<boolean>(true);
  const [error, seterror] = useState<null | string>(null);

  const stringifiedOptions = useMemo(() => JSON.stringify(options), [options]);

  let ref: any =
    collectionPath && !collectionPath.includes('//')
      ? db.collection(collectionPath)
      : null;

  useEffect(() => {
    let active = true;
    let unsubscribe: undefined | (() => void);
    if (ref) {
      setloading(true);
      if (!options.keepDataWhileLoading) {
        setdata([]);
      }
      seterror(null);

      filter.forEach((rule: Triplet) => {
        // eslint-disable-next-line
        ref = ref?.where(...rule);
      });

      if (sort.length) {
        ref = ref?.orderBy(...sort);
      }

      if (limit) {
        ref = ref?.limit(limit);
      }

      // Captured at subscribe time: a listener attached before auth restored
      // the session is denied server-side even when the restore finishes
      // before the denial arrives back.
      const noUserAtSubscribe = !firebase.auth().currentUser;

      unsubscribe = ref?.onSnapshot(
        (querySnapshots: firebase.firestore.QuerySnapshot<T>) => {
          if (!active) return;
          let arr: (T & { id: string })[] = [];

          querySnapshots.forEach((doc) => {
            let data = { ...doc.data(), id: doc.id };

            arr.push(data);
          });

          setdata(arr);
          setloading(false);
        },
        (err: firebase.firestore.FirestoreError) => {
          if (!active) return;
          // Two expected cases, kept out of Sentry: the query ran before its
          // scoping id (e.g. the restaurantId from context) had resolved, so
          // the rules cannot scope it and always deny (the effect re-runs once
          // the id arrives), or the listener attached before auth restored the
          // session and was denied server-side. Same reasoning as
          // silenceExpectedPermissionErrors in useDocument. Real denials for a
          // signed-in user with resolved values still report.
          const suppressExpectedPermissionDenied =
            err.code === 'permission-denied' &&
            (noUserAtSubscribe ||
              filter.some(([, , value]) => value == null || value === ''));
          if (!suppressExpectedPermissionDenied) {
            // Filter fields only, not values — values can contain guest data.
            const queriedFields = filter
              .map(([field, op]) => `${field} ${op} …`)
              .join(', ');
            logFirestoreError(
              'useCollection',
              queriedFields
                ? `${collectionPath} [${queriedFields}]`
                : collectionPath,
              err
            );
          }
          seterror(err.message);
          setloading(false);
        }
      );

    }
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [db, collectionPath, stringifiedOptions]);

  return [{ ref, data, loading, error }, setdata];
}

export default useCollection;
